<template>
  <PaymentPlanResolver :fetch="setPlan" :id="id">
    <template v-slot:default="{plan}">
      <div class="plan-header">
        <VCardTitle class="plan-title">
          <RouterLink to="/settings/payment/plans" class="plan-title__link">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="black" />
            </svg>
          </RouterLink>
          {{ isExpired ? $t('renewPlan') : $t('changePlan') }}
        </VCardTitle>
      </div>
      <VCardSubtitle class="plan-subtitle">
        {{ $t('subject',{plan: plan.title}) }}
      </VCardSubtitle>
      <VCardText>
        <PaymentDialogPeriods @input="period = $event" :plan="plan" />
        <PaymentMethodsSelector v-if="period" @input="onPaymentMethodChange">
          <template #footer>
            <StripeUpgradePayment
              @success="onSuccess"
              :period="period"
              :plan="plan"
              :method-id="method"
              :new-card="!hasPaymentMethods || needNewCard"
              v-if="period && driver === paymentDrivers.STRIPE"
            />
            <AlfabankUpgradePayment
              :plan="plan"
              :period="period"
              @success="onSuccess"
              :method-id="method"
              :new-card="!hasPaymentMethods || needNewCard"
              v-if="driver === paymentDrivers.ALFABANK"
            />
          </template>
        </PaymentMethodsSelector>
      </VCardText>
    </template>
  </PaymentPlanResolver>
</template>
<script>
import usePayment from '@/compositions/accounting/usePayment';
import { orderPlanOperations, orderTypes, paymentDrivers } from '@/enums';
import StripeUpgradePayment from '@/components/Payment/Payment/Stripe/StripeUpgradePayment';
import PaymentDialogPeriods from '@/components/Payment/Periods/PaymentDialogPeriods';
import { computed, onMounted, ref } from '@vue/composition-api';
import PaymentMethodsSelector from '@/components/Payment/Methods/PaymentMethodsCardSelector';
import usePaymentMethods from '@/compositions/accounting/usePaymentMethods';
import PaymentPlanResolver from '@/components/Payment/Plans/PaymentPlanResolver';
import useSubscription from '@/compositions/accounting/useSubscription';
import useAccount from '@/compositions/accounting/useAccount';
import AlfabankUpgradePayment from '@/components/Payment/Payment/Alfabank/AlfabankUpgradePayment';

export default {
  name: 'PaymentDialog',
  components: {
    AlfabankUpgradePayment,
    PaymentPlanResolver,
    PaymentMethodsSelector,
    PaymentDialogPeriods,
    StripeUpgradePayment,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  setup(_, { root: { $router, $route } }) {
    const step = ref(1);
    const method = ref(null);
    const { isExpired } = useSubscription();
    const {
      paymentDialog, driver, setPeriod,
    } = usePayment();

    const { period: currentPeriod } = useAccount();

    const periodRef = ref(null);
    const plan = ref(null);
    const period = computed({
      get() {
        return periodRef.value || currentPeriod.value;
      },
      set(val) {
        periodRef.value = val;
      },
    });
    const needNewCard = ref(null);

    const { paymentMethods, fetchAll } = usePaymentMethods();
    fetchAll();

    setPeriod(null);

    const stepValid = computed(() => {
      switch (step.value) {
        case 1:
          return !!period.value;
        default:
          return false;
      }
    });

    function onSuccess() {
      $router.replace({
        name: 'order-success',
        params: {
          id: plan.value.id,
          operation: $route.query.renew ? orderPlanOperations.RENEW : orderPlanOperations.UPGRADE,
          type: orderTypes.PLAN,
        },
      });
    }

    function setPlan(val) {
      plan.value = val;
    }

    function onPaymentMethodChange(val) {
      if (val !== 'new') {
        method.value = val;
        setPeriod(period.value);
        needNewCard.value = false;
        return;
      }
      method.value = null;
      setPeriod(period.value);
      needNewCard.value = true;
    }

    return {
      onSuccess,
      paymentDialog,
      driver,
      paymentDrivers,
      period,
      plan,
      step,
      stepValid,
      method,
      onPaymentMethodChange,
      paymentMethods,
      hasPaymentMethods: computed(() => {
        return !!paymentMethods.value?.length > 0;
      }),
      needNewCard,
      isExpired,
      setPlan,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-title {
  font-size: 1.5rem;
  line-height: 1.2;
  color: #212121;
  @media screen and (max-width: 428px) {
    font-size: 18px;
  }
  &__link {
    display: inline-block;
    height: 1.5rem;
    vertical-align: middle;
    margin-right: 0.25rem;
  }
}
.plan-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media screen and (max-width: 1200px) {
    flex-direction: column;
    align-items: flex-start;
  }
}
.plan-subtitle {
  padding-top: 1rem;
}

</style>

<i18n lang="yml" locale="ru">
changePlan: Изменение тарифного плана
subject: 'Вы переходите на тарифный план «{plan}»'
renewPlan: Возобновление подписки
</i18n>
<i18n lang="yml" locale="en">
changePlan: Change plan
subject: 'Your new plan is “{plan}”'
renewPlan: Renew subscription
</i18n>
